import * as math from 'mathjs';
import Fkin from "./Fkin";
import RT_R6_q4_q5_q6 from "./RT_R6_q4_q5_q6";

class R6RTrafo {
  run(F_akt, q_alt, r) {
    let fkin = new Fkin();
    let rtR6_q4_q5_q6 = new RT_R6_q4_q5_q6();

    // Extract position and orientation from F_akt (3x4 or 4x4 frame)
    let F_akt_squeezed = math.squeeze(F_akt);
    let p_06 = math.subset(F_akt_squeezed, math.index([0, 1, 2], 3)); // F_akt(:,4) -> column 3 (0-based)
    let A60 = math.subset(F_akt_squeezed, math.index([0, 1, 2], [0, 1, 2])); // F_akt(:,1:3) -> columns 0,1,2

    // Normalize A60 columns to unit vectors
    let ltcp = math.subset(A60, math.index([0, 1, 2], 0));
    ltcp = math.squeeze(ltcp);
    let ltcp_arr = ltcp.toArray ? ltcp.toArray() : (Array.isArray(ltcp) ? ltcp : [ltcp]);
    let ltcp_norm = math.norm(ltcp_arr);
    if (ltcp_norm > 1e-10) {
      ltcp_arr = ltcp_arr.map(v => v / ltcp_norm);
    }
    ltcp = math.matrix(ltcp_arr);
    
    let mtcp = math.subset(A60, math.index([0, 1, 2], 1));
    mtcp = math.squeeze(mtcp);
    let mtcp_arr = mtcp.toArray ? mtcp.toArray() : (Array.isArray(mtcp) ? mtcp : [mtcp]);
    let mtcp_norm = math.norm(mtcp_arr);
    if (mtcp_norm > 1e-10) {
      mtcp_arr = mtcp_arr.map(v => v / mtcp_norm);
    }
    mtcp = math.matrix(mtcp_arr);
    
    let ntcp = math.subset(A60, math.index([0, 1, 2], 2));
    ntcp = math.squeeze(ntcp);
    let ntcp_arr = ntcp.toArray ? ntcp.toArray() : (Array.isArray(ntcp) ? ntcp : [ntcp]);
    let ntcp_norm = math.norm(ntcp_arr);
    if (ntcp_norm > 1e-10) {
      ntcp_arr = ntcp_arr.map(v => v / ntcp_norm);
    }
    ntcp = math.matrix(ntcp_arr);

    // NEU: sicherheitshalber wird A60 normiert verwendet
    // ltcp_arr, mtcp_arr, ntcp_arr are already arrays from above
    A60 = math.matrix([
      [ltcp_arr[0], mtcp_arr[0], ntcp_arr[0]],
      [ltcp_arr[1], mtcp_arr[1], ntcp_arr[1]],
      [ltcp_arr[2], mtcp_arr[2], ntcp_arr[2]]
    ]);

    let d = r.joints.map(j => j.d);
    let a = r.joints.map(j => j.a);
    let alpha = r.joints.map(j => j.alpha);

    let qmax = r.joints.map(j => j.qmax);
    let qmin = r.joints.map(j => j.qmin);

    // NEU: April 2023 - Festlegung von Grenzwerten
    let eps_q5_krit = 2 * Math.PI / 180; // kritischer Bereich für sing. Stellung
    let eps_q5 = 0.00005; // für |q5| < eps_q5 Sonderbehandlung, da q5-Singularität
    let phi1 = 0.1 * Math.PI / 180; // im krit. Bereich Sing. |q5|<eps_q5_krit wird K6 um phi um y6-Achse gedreht

    let warnung = '';
    let qz;

    // p04 = p_06 - ntcp * d(6) -> d[5] in JS (0-based)
    let ntcp_squeezed = math.squeeze(ntcp);
    let ntcp_arr_for_p04 = ntcp_squeezed.toArray ? ntcp_squeezed.toArray() : (Array.isArray(ntcp_squeezed) ? ntcp_squeezed : [ntcp_squeezed]);
    let p_06_squeezed = math.squeeze(p_06);
    let p_06_arr = p_06_squeezed.toArray ? p_06_squeezed.toArray() : (Array.isArray(p_06_squeezed) ? p_06_squeezed : [p_06_squeezed]);
    let p04 = math.subtract(
      math.matrix(p_06_arr),
      math.multiply(math.matrix(ntcp_arr_for_p04), d[5]) // d(6) in MATLAB = d[5] in JS
    );

    // q1a = atan2(p04(2), p04(1)) -> atan2(p04[1], p04[0]) in JS
    let p04_arr = math.squeeze(p04).toArray ? math.squeeze(p04).toArray() : (Array.isArray(math.squeeze(p04)) ? math.squeeze(p04) : [math.squeeze(p04)]);
    let q1a = Math.atan2(p04_arr[1], p04_arr[0]); // p04(2) -> p04[1], p04(1) -> p04[0]

    let q1b;
    if (q1a >= 0) {
      q1b = q1a - Math.PI;
    } else {
      q1b = q1a + Math.PI;
    }

    // p14a = p04 - [a(1)*cos(q1a); a(1)*sin(q1a); d(1)] -> a[0], d[0] in JS
    let p14a = math.subtract(
      math.matrix(p04_arr),
      math.matrix([a[0] * Math.cos(q1a), a[0] * Math.sin(q1a), d[0]])
    );
    let p14b = math.subtract(
      math.matrix(p04_arr),
      math.matrix([a[0] * Math.cos(q1b), a[0] * Math.sin(q1b), d[0]])
    );

    // betr_quad_p14 = p14a' * p14a
    let p14a_squeezed = math.squeeze(p14a);
    let p14a_arr = p14a_squeezed.toArray ? p14a_squeezed.toArray() : (Array.isArray(p14a_squeezed) ? p14a_squeezed : [p14a_squeezed]);
    let betr_quad_p14 = math.multiply(math.transpose(math.matrix(p14a_arr)), math.matrix(p14a_arr));
    betr_quad_p14 = typeof betr_quad_p14 === 'number' ? betr_quad_p14 : (betr_quad_p14 && betr_quad_p14.valueOf ? betr_quad_p14.valueOf() : parseFloat(betr_quad_p14));

    // AR_test_aussen = (a(2) + d(4))^2 - betr_quad_p14 -> a[1] + d[3] in JS
    let AR_test_aussen = Math.pow(a[1] + d[3], 2) - betr_quad_p14;

    let AR_test_innen;
    if (d[3] < a[1]) { // d(4) < a(2) -> d[3] < a[1] in JS
      AR_test_innen = betr_quad_p14 - Math.pow(a[1] - d[3], 2);
    } else {
      AR_test_innen = 25;
    }

    if (AR_test_aussen >= 0 && AR_test_innen >= 0) {
      // phia = acos((a(2)^2 + d(4)^2 - betr_quad_p14)/(2 * a(2) * d(4))) -> a[1], d[3] in JS
      let phia = Math.acos((Math.pow(a[1], 2) + Math.pow(d[3], 2) - betr_quad_p14) / (2 * a[1] * d[3]));

      // Calculate q2, q3 for configuration space 1 (q1a)
      let q_akt_temp = [q1a, 0, 0, 0, 0, 0];
      let [T, T0] = fkin.run(r, q_akt_temp);
      
      // T(:,:,1) -> index 0 (0-based) for 1st frame - MATLAB uses T, not T0!
      let T_size = T.size();
      let T10a;
      if (T_size.length === 2) {
        T10a = T;
      } else if (T_size.length === 3 && T_size[2] > 0) {
        T10a = math.squeeze(math.subset(T, math.index([0, 1, 2, 3], [0, 1, 2, 3], 0)));
      } else {
        T10a = math.squeeze(math.subset(T, math.index([0, 1, 2, 3], [0, 1, 2, 3], T_size[2] - 1)));
      }
      
      // A10a = T10a(1:3,1:3) -> rows 0,1,2, cols 0,1,2
      let A10a = math.subset(T10a, math.index([0, 1, 2], [0, 1, 2]));
      let A01a = math.transpose(A10a);
      
      // p141a = A01a * p14a
      let p141a = math.multiply(A01a, math.matrix(p14a_arr));
      let p141a_arr = math.squeeze(p141a).toArray ? math.squeeze(p141a).toArray() : (Array.isArray(math.squeeze(p141a)) ? math.squeeze(p141a) : [math.squeeze(p141a)]);
      
      // beta1 = atan2(p141a(2), p141a(1)) -> atan2(p141a[1], p141a[0]) in JS
      let beta1 = Math.atan2(p141a_arr[1], p141a_arr[0]);
      
      // beta2 = acos((a(2)^2 + betr_quad_p14 - d(4)^2) / (2*sqrt(betr_quad_p14) * a(2))) -> a[1], d[3] in JS
      let beta2 = Math.acos((Math.pow(a[1], 2) + betr_quad_p14 - Math.pow(d[3], 2)) / (2 * Math.sqrt(betr_quad_p14) * a[1]));

      let q3a = 1.5 * Math.PI - phia;
      let q3b = 1.5 * Math.PI + phia;
      let q2a = beta1 - beta2;
      let q2b = beta1 + beta2;

      // -------------------------------------------------------------------------------------
      // Loesungen q4a, q4b, q5a, q5b, q6a, q6b auf der Basis von q1a, q2a, q3a
      let q_akt = [q1a, q2a, q3a, 0, 0, 0];
      let [q4_1, q4_2, q5_1, q5_2, q6_1, q6_2] = rtR6_q4_q5_q6.run(q_akt, A60, p_06, r, eps_q5, eps_q5_krit, phi1, q_alt);
      let q4a = q4_1;
      let q4b = q4_2;
      let q5a = q5_1;
      let q5b = q5_2;
      let q6a = q6_1;
      let q6b = q6_2;

      // -------------------------------------------------------------------------------------
      // Loesungen q4c, q4d, q5c, q5d, q6c, q6d auf der Basis von q1a, q2b, q3b
      q_akt = [q1a, q2b, q3b, 0, 0, 0];
      [q4_1, q4_2, q5_1, q5_2, q6_1, q6_2] = rtR6_q4_q5_q6.run(q_akt, A60, p_06, r, eps_q5, eps_q5_krit, phi1, q_alt);
      let q4c = q4_1;
      let q4d = q4_2;
      let q5c = q5_1;
      let q5d = q5_2;
      let q6c = q6_1;
      let q6d = q6_2;

      // Calculate q2, q3 for configuration space 2 (q1b)
      let phib = Math.acos((Math.pow(a[1], 2) + Math.pow(d[3], 2) - betr_quad_p14) / (2 * a[1] * d[3]));

      q_akt_temp = [q1b, 0, 0, 0, 0, 0];
      let fKinTmp = fkin.run(r, q_akt_temp);
      T = fKinTmp[0];
      T0 = fKinTmp[1];
      
      // T(:,:,1) -> index 0 (0-based) for 1st frame - MATLAB uses T, not T0!
      T_size = T.size();
      let T10b;
      if (T_size.length === 2) {
        T10b = T;
      } else if (T_size.length === 3 && T_size[2] > 0) {
        T10b = math.squeeze(math.subset(T, math.index([0, 1, 2, 3], [0, 1, 2, 3], 0)));
      } else {
        T10b = math.squeeze(math.subset(T, math.index([0, 1, 2, 3], [0, 1, 2, 3], T_size[2] - 1)));
      }
      
      // A10b = T10b(1:3,1:3) -> rows 0,1,2, cols 0,1,2
      let A10b = math.subset(T10b, math.index([0, 1, 2], [0, 1, 2]));
      let A01b = math.transpose(A10b);
      
      // p141b = A01b * p14b
      let p14b_squeezed = math.squeeze(p14b);
      let p14b_arr = p14b_squeezed.toArray ? p14b_squeezed.toArray() : (Array.isArray(p14b_squeezed) ? p14b_squeezed : [p14b_squeezed]);
      let p141b = math.multiply(A01b, math.matrix(p14b_arr));
      let p141b_arr = math.squeeze(p141b).toArray ? math.squeeze(p141b).toArray() : (Array.isArray(math.squeeze(p141b)) ? math.squeeze(p141b) : [math.squeeze(p141b)]);
      
      // beta1 = atan2(p141b(2), p141b(1)) -> atan2(p141b[1], p141b[0]) in JS
      beta1 = Math.atan2(p141b_arr[1], p141b_arr[0]);
      
      // beta2 = acos((a(2)^2+betr_quad_p14-d(4)^2)/(2*sqrt(betr_quad_p14)*a(2))) -> a[1], d[3] in JS
      beta2 = Math.acos((Math.pow(a[1], 2) + betr_quad_p14 - Math.pow(d[3], 2)) / (2 * Math.sqrt(betr_quad_p14) * a[1]));

      let q3c = 1.5 * Math.PI - phib;
      let q3d = 1.5 * Math.PI + phib;
      let q2c = beta1 - beta2;
      let q2d = beta1 + beta2;

      // -------------------------------------------------------------------------------------
      // Loesungen q4e, q4f, q5e, q5f, q6e, q6f auf der Basis von q1b, q2c, q3c
      q_akt = [q1b, q2c, q3c, 0, 0, 0];
      [q4_1, q4_2, q5_1, q5_2, q6_1, q6_2] = rtR6_q4_q5_q6.run(q_akt, A60, p_06, r, eps_q5, eps_q5_krit, phi1, q_alt);
      let q4e = q4_1;
      let q4f = q4_2;
      let q5e = q5_1;
      let q5f = q5_2;
      let q6e = q6_1;
      let q6f = q6_2;

      // -------------------------------------------------------------------------------------
      // Loesungen q4g, q4h, q5g, q5h, q6g, q6h auf der Basis von q1b, q2d, q3d
      q_akt = [q1b, q2d, q3d, 0, 0, 0];
      [q4_1, q4_2, q5_1, q5_2, q6_1, q6_2] = rtR6_q4_q5_q6.run(q_akt, A60, p_06, r, eps_q5, eps_q5_krit, phi1, q_alt);
      let q4g = q4_1;
      let q4h = q4_2;
      let q5g = q5_1;
      let q5h = q5_2;
      let q6g = q6_1;
      let q6h = q6_2;

      // Build solution matrix qz
      // MATLAB: qz = [q1a, q1a, q1a, q1a, q1b, q1b, q1b, q1b; ...]
      let qz_data = [
        [q1a, q1a, q1a, q1a, q1b, q1b, q1b, q1b],
        [q2a, q2a, q2b, q2b, q2c, q2c, q2d, q2d],
        [q3a, q3a, q3b, q3b, q3c, q3c, q3d, q3d],
        [q4a, q4b, q4c, q4d, q4e, q4f, q4g, q4h],
        [q5a, q5b, q5c, q5d, q5e, q5f, q5g, q5h],
        [q6a, q6b, q6c, q6d, q6e, q6f, q6g, q6h]
      ];
      qz = math.matrix(qz_data);
    } else {
      // TCP ausserhalb des AR ohne Berücksichtigung der Gelenkbeschraenkungen
      warnung = 'ausserhalb';
      qz = [];
    }
    return qz;
  }
}

export default R6RTrafo;
